import { html } from 'lit';
import { ViewBase } from '../view-base.js';
import { auth, signInWithCustomToken } from '../../firebaseConfig.js';

import { registerViewStyles } from './register-view-styles.js';

import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';

import { FormHostMixin } from '../../modules/form-host-mixin.js';
import { formatFirebaseError } from '../../modules/firebase-error-helper.js';
import { q1RegistrationFunctions } from '../../modules/q1-core-functions.js'
import { Q1TelInput } from '../../components/q1-tel-input/q1-tel-input.js';

const PHONE_HELP_TEXT = 'Used for important comms. '
  + 'By providing your phone number you are consenting to receive occasional text messages '
  + 'from Quorum1 and the q1 network, according to your preferences.';

export class RegisterView extends FormHostMixin(ViewBase) {
  static styles = [
    ...super.styles,
    registerViewStyles,
  ];

  static properties = {
    ...super.properties,

    nameInternal: { type: String },
    nameExternal: { type: String },
    emailPrimary: { type: String },
    emailsAdditional: { type: Array },
    emailsAdditionalText: { type: String },
    linkedinProfileUrl: { type: String },
    phoneMobile: { type: String },
    password: { type: String },
    nameLegal: { type: String },
    
    existingMemberStatus: { type: String },

    viewMode: { type: String }, // ready, processing, form-error
    errorMessage: { type: String },
  };

  constructor() {
    super();

    this.nameInternal = this.urlParams.name || '';
    this.nameExternal = this.urlParams.name || '';
    this.emailPrimary = this.urlParams.email || '';
    this.emailsAdditional = [];
    this.emailsAdditionalText = '';
    this.linkedinProfileUrl = '';
    this.phoneMobile = this.urlParams.phone || '';
    this.password = '';
    this.nameLegal = this.urlParams.name || '';

    this.existingMemberStatus = null;

    this.viewMode = 'ready';
  }
  
  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();
  }

  get handleInputPostProcess() {
    return {
      emailsAdditionalText: (newValue) => this.handleEmailsAdditionalTextChange(newValue),
    };
  }

  handleEmailsAdditionalTextChange(newValue = '') {
    this.emailsAdditional = [
      ...new Set(
        newValue
          .split(',')
          .map(e => e.trim().toLowerCase())
          .filter(e => e.includes('@'))
      )
    ];
  }

  handleEmailPrimaryChange(event) {
    const emailPrimary = event.target.value;
    if (!emailPrimary) return;

    const { checkForExistingMember } = q1RegistrationFunctions;

    checkForExistingMember({ email: emailPrimary }).then((response) => {
      const responseData = response.data || {};
      this.existingMemberStatus = responseData?.existingMemberStatus || 'none';

      if (this.existingMemberStatus !== 'none') this.showExistingMemberDialog();
    }).catch((error) => {
      console.error('Error checking for existing member:', error.message);
    });
  }
  
  handleRegistrationFormSubmit(event) {
    const { newMember } = q1RegistrationFunctions;
    event.preventDefault();
    const registrationForm = this.renderRoot.querySelector('#registration-form');
    
    if (!registrationForm.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      return;
    }

    this.viewMode = 'processing';
    this.errorMessage = '';

    const registrationFormData = {
      ...this._serializeForm(registrationForm),
      emailsAdditional: this.emailsAdditional,
    };
    console.log('REGISTRATION SUBMIT. registrationFormData: ', registrationFormData);
    
    newMember(registrationFormData).then((result) => {
      console.log('newMember result: ', result);

      const authToken = result?.data?.authToken;

      signInWithCustomToken(auth, authToken).then((userCredential) => {
        console.log('Sign in successful, userCredential: ', userCredential);
        
        document.querySelector('q1-home').queueToast(
          'The next step is to finish confirming your account',
          'Member registration received!', 'success'
        );
        
        // NOTE: We want to trigger a full reload
        window.location.href = '/home';
      }).catch((error => {
        error = formatFirebaseError(error);
        this.errorMessage = `Error signing in with token. Message: ${error}`;
        console.log(this.errorMessage);
        this.viewMode = 'form-error';
      }));
    }).catch((error) => {
      console.error('Error calling function:', error.message);

      this.showToast('There was an error submitting your registration!', null, 'danger');
      this.errorMessage = error;
      this.viewMode = 'form-error';
    });
  }

  showExistingMemberDialog() {
    const existingMemberDialog = this.renderRoot.getElementById('existing-member-dialog');
    existingMemberDialog.show();
  }

  hideExistingMemberDialog() {
    const existingMemberDialog = this.renderRoot.getElementById('existing-member-dialog');
    existingMemberDialog.hide();
  }

  get headerTemplate() {
    return html`
      <q1-app-header
        hide-center-nav
        hide-right-nav
      ></q1-app-header>
    `;
  }

  get contentTemplate() {
    return html`
      <header-bar class="with-border">
        <h1>
          <span class="prefix">q1 Network + Quorum1</span>
          New Member Registration
        </h1>

        <sl-icon class="header-icon" library="vectors" name="growth"></sl-icon>
      </header-bar>

      <p class="subtitle hero primary">
        We're excited to collaborate with you! 
        Fill out the form below to register in the q1 Network
        and become a member of the Quorum1 collective.
      </p>

      ${this.viewMode !== 'processing' ? null : html`
        <sl-alert variant="primary" open>
          <sl-spinner slot=icon style="font-size: 1.5rem;"></sl-spinner>
          <strong>Processing your registration...</strong><br/>
        </sl-alert>
      `}

      ${this.formTemplate}

      ${this.existingMemberDialogTemplate}
    `;
  }

  get formTemplate() {
    return html`
      <form id="registration-form" class="q1" @submit=${this.handleRegistrationFormSubmit}>
        ${this.viewMode !== 'form-error' ? null : html`
          <sl-alert variant="danger" open>
            <sl-icon library="material" slot="icon" name="warning"></sl-icon>
            <strong>An error occurred in your member registration.</strong><br/>
            
            ${this.errorMessage}
          </sl-alert>
        `}
        
        <h2>Your Account</h2>
        
        <field-pair>
          <sl-input 
            id="nameInternal" 
            name="nameInternal" 
            .value="${this.nameInternal}"
            @sl-input="${this.handleInput}"
            label="Internal Name" 
            help-text="What other Quorum1 members see"
            required
          ></sl-input>

          <sl-input 
            id="nameExternal" 
            name="nameExternal" 
            .value="${this.nameExternal}"
            @sl-input="${this.handleInput}"
            label="External Name" 
          >
            <span slot="help-text">
              What is shown publicly to non-members.
              Set this field to a blank value to be externally anonymous.
            </span>
          </sl-input>
        </field-pair>
        
        <field-pair>
          <sl-input 
            id="emailPrimary"
            name="emailPrimary"
            .value=${this.emailPrimary}
            @sl-input=${this.handleInput}
            @sl-change=${this.handleEmailPrimaryChange}
            type="email"
            label="Primary Email" 
            autocomplete="username" 
            required
            exportparts="input"
          ></sl-input>
          
          <!-- No name attribute. This is processed before passing in the form data. -->
          <sl-input 
            id="emailsAdditionalText"
            .value="${this.emailsAdditionalText}"
            @sl-input="${this.handleInput}"
            label="Additional Emails" 
            help-text="Link your other emails to help collaborators find you. Separate with commas."
          ></sl-input>
        </field-pair>
        
        <field-pair>
          <sl-input 
            id="linkedinProfileUrl"
            name="linkedinProfileUrl"
            .value="${this.linkedinProfileUrl}"
            @sl-input="${this.handleInput}"
            type="url"
            label="LinkedIn Profile URL" 
            pattern="https?:\/\/.*linkedin\.com\/in\/.*"
            required
          >
            <span slot="help-text">
              Required to verify your professional identity.
              Reach out to team@quorum.one if you don't have a LI profile.
            </span>
          </sl-input>

          <q1-tel-input
            id="phoneMobile"
            name="phoneMobile"
            .value="${this.phoneMobile}"
            @input="${this.handleInput}"
            label="Mobile Phone"
            help-text=${PHONE_HELP_TEXT}
            required
          ></q1-tel-input>
        </field-pair>

        <field-pair>
          <sl-input 
            id="password"
            name="password"
            .value="${this.password}"
            @sl-input="${this.handleInput}"
            label="Password" 
            type="password"
            required
            minlength="8"
            password-toggle
            autocomplete="new-password"
          ></sl-input>
          <field-spacer></field-spacer>
        </field-pair>

        <sl-divider></sl-divider>

        <h2>Legal Agreement</h2>
        
        <field-pair>
          <sl-input 
            id="nameLegal"
            name="nameLegal"
            .value="${this.nameLegal}"
            @sl-input="${this.handleInput}"
            label="Legal Name" 
            help-text="Used when you sign agreements, kept private otherwise"
            required
          >
            <sl-icon library="material" name="signature" slot="prefix"></sl-icon>
            <sl-icon library="material" name="lock" slot="suffix"></sl-icon>
          </sl-input>
          <field-spacer></field-spacer>
        </field-pair>

        <p>
          By joining Quorum1 you are agreeing to our
          <a target="_blank"
            href="https://github.com/quorum1/governance/blob/main/docs/community-agreement.md"
          >Member Agreement</a>
          and 
          <a target="_blank"
            href="https://github.com/quorum1/governance/blob/main/docs/terms-of-service.md"
          >Terms of Service</a>.
        </p>

        <!-- FIXME - Add recaptcha -->
        
        <sl-divider></sl-divider>

        <sl-button
          variant="primary"
          size="large"
          type="submit"
          pill
          ?disabled="${this.viewMode === 'processing'}"
        >
          Save &amp; Continue
          <sl-icon library="material" name="arrow_forward" slot="suffix"></sl-icon>
        </sl-button>
      </form>
    `;
  }

  get existingMemberDialogTemplate() {
    return html`
      <sl-dialog
        id="existing-member-dialog"
        label="Hold up! Looks like you're already a member..."
      >
        <div class="dialog-content">
          ${this.existingMemberStatus === 'provisioned' ? html`
            <p>
              Your email address is already associated with a Quorum1 membership that 
              has yet to be claimed and activated in the new q1 Network platform.
            </p>
            <p>
              Please click the button below to claim your membership and activate your
              q1 Network account.
            </p>
          ` : html`
            <p>
              Your email address is already associated with an active Quorum1 membership.
            </p>
            <p>
              Please click the button below to continue to the sign in screen.
            </p>
          `}
        </div>

        <button-bar slot="footer">
          <sl-button
            variant="default"
            @click="${this.hideExistingMemberDialog}"
          >Close</sl-button>
          
          ${this.existingMemberStatus === 'provisioned' ? html`
            <sl-button
              variant="warning"
              href="/claim?email=${this.emailPrimary}"
            >
              Claim your Membership
            </sl-button>
          ` : html`
            <sl-button
              variant="primary"
              href="/signin?email=${this.emailPrimary}"
            >
              Continue to Sign In
            </sl-button>
          `}
        </button-bar>
      </sl-dialog>
    `;
  }
}

customElements.define('register-view', RegisterView);
